import { CircularProgress, Stack, Typography } from '@mui/material'
import { sourcesUpdateCalendars } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { Feature, SourceCalendar, SourceStatus } from 'api/model'
import { COLORS } from 'app-constants'
import FtuxTooltip from 'components/FtuxTooltip'
import JelledSwitch from 'components/JelledSwitch'
import SetupPermissionsModal, { PermissionsButton } from 'components/Permissions/SetupPermissionsModal'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useFtuxTooltip } from 'hooks/use-ftux'
import { useActiveOrganization } from 'hooks/use-organization'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import { useFetchSourceCalendars } from 'hooks/use-sources'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'

const SourceCalendarRow = ({ sourceCalendar }: { sourceCalendar: SourceCalendar }) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [isCalendarSynced, setIsCalendarSynced] = useState<SourceStatus>(sourceCalendar.status)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onCalendarSyncChange = async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsCalendarSynced(checked ? SourceStatus.active : SourceStatus.inactive)
    setIsSubmitting(true)

    if (!activeOrganization) {
      setIsSubmitting(false)
      return
    }

    const calendarUpdate = [
      {
        id: sourceCalendar.id,
        status: checked ? SourceStatus.active : SourceStatus.inactive,
      },
    ]

    try {
      await sourcesUpdateCalendars(activeOrganization, calendarUpdate)
      if (checked) {
        setToast({ title: `Your twin is now synced with your ${sourceCalendar.name}`, severity: 'success' })
      } else {
        setToast({ title: `Your twin is no longer synced with your ${sourceCalendar.name}`, severity: 'success' })
      }
    } catch (Error) {
      setToast({ title: `Failed to update the sync status with your ${sourceCalendar.name}`, severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <JelledSwitch
      disabled={isSubmitting}
      label={`Sync ${sourceCalendar.name}`}
      checked={isCalendarSynced === SourceStatus.active}
      onChange={onCalendarSyncChange}
    />
  )
}

const MyTwinCalendars = () => {
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()

  const { isLoading, sourceCalendars } = useFetchSourceCalendars()
  const { isFtuxTooltipOpen, setIsFtuxTooltipOpen } = useFtuxTooltip()

  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)
  const onPermissionsButtonClick = () => {
    setIsPermissionsModalOpen(true)
  }
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }
  const { featurePermissions } = useFetchCheckPermissions(Feature.google_calendar)
  const hasPermissions = featurePermissions?.isPermitted ?? false
  const acceptedPermissions = featurePermissions?.acceptedPermissions ?? []
  const missingPermissions = featurePermissions?.missingPermissions ?? []

  const renderContent = () => {
    if (isLoading) {
      return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
    }

    if (!hasPermissions) {
      return (
        <>
          <PermissionsButton onClick={onPermissionsButtonClick} />
          {isPermissionsModalOpen && (
            <SetupPermissionsModal
              onClose={onClosePermissionsModal}
              acceptedPermissions={acceptedPermissions}
              missingPermissions={missingPermissions}
              feature={Feature.google_calendar}
            />
          )}
        </>
      )
    }

    if (sourceCalendars.length === 0) {
      return (
        <Typography variant="pS" mb={2}>
          No calendars found. Please add a calendar to your Google account.
        </Typography>
      )
    }

    return (
      <FtuxTooltip
        open={isFtuxTooltipOpen}
        setOpen={setIsFtuxTooltipOpen}
        headerText="Sync calendar to add events"
        subtitleText="Sync your Google Calendar to enable your twin to answer questions around specific events on your calendar. Syncing is on by default."
        progressText="4 of 6"
        onBackClick={() => {
          navigate('/my-twin/emails')
        }}
        onNextClick={() => {
          navigate('/my-twin/files')
        }}
        placement="bottom"
        onDismiss={() => {
          setIsFtuxTooltipOpen(false)
        }}>
        <Stack spacing={1} maxWidth="360px" mt={1}>
          {sourceCalendars.map((sourceCalendar) => (
            <SourceCalendarRow sourceCalendar={sourceCalendar} key={sourceCalendar.id} />
          ))}
        </Stack>
      </FtuxTooltip>
    )
  }

  return (
    <Stack
      borderLeft="1px solid #DDDDDD"
      width="100%"
      height="100vh"
      sx={{
        background: COLORS.GRAY_8,
        p: 3,
      }}>
      {!isMobile && (
        <Typography variant="h4" mb={2}>
          Calendars
        </Typography>
      )}

      <Typography
        variant="pS"
        mb={1}
        sx={{
          color: COLORS.GRAY_2,
        }}>
        Sync your calendar to allow your twin to answer questions relating to event names and times.
      </Typography>

      {renderContent()}
    </Stack>
  )
}

export default MyTwinCalendars
